import React, { useState, useEffect, useRef } from 'react';
import { AlertTriangle, Search } from 'lucide-react';
import api from '../api/axios';
import { formatApiError } from '../api/errors';

const MAX_BULK_ACCOUNTS = 500;

// No <form> has this id. Controls that save on their own point their `form` attribute here
// so they sit outside the subscription form's validation and implicit submission.
export const STANDALONE_CONTROL_FORM = 'low-balance-override-controls';

// Blank -> null (no value), anything else -> a number that may be NaN or negative.
export const parseAmount = (text) => {
  const trimmed = String(text ?? '').trim();
  return trimmed === '' ? null : Number(trimmed);
};
export const isValidAmount = (n) => Number.isFinite(n) && n >= 0;

export const formatCurrency = (amount) => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    // Whole dollars stay compact; amounts with cents always show two decimals ($2,500.50).
    minimumFractionDigits: Number.isInteger(amount) ? 0 : 2
  }).format(amount);
};

const formatBalance = (amount) =>
  new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(amount || 0);

const formatChangedAt = (value) => {
  if (!value) return '';
  const parsed = new Date(value);
  return isNaN(parsed.getTime()) ? '' : parsed.toLocaleString();
};

// Operating accounts and their shared low-balance overrides. Overrides save immediately,
// independent of the subscription form's Save button. Mounted only while the subscription
// opts in, so it loads fresh each time it appears.
const OperatingThresholdsTable = ({ allFirmsSelected, selectedFirms, defaultThreshold }) => {
  const [operatingAccounts, setOperatingAccounts] = useState([]);
  const [operatingLoading, setOperatingLoading] = useState(false);
  const [operatingError, setOperatingError] = useState(null);
  const [operatingSearch, setOperatingSearch] = useState('');
  const [operatingFirmFilter, setOperatingFirmFilter] = useState('');
  const [selectedAccountIds, setSelectedAccountIds] = useState([]);
  const [overrideDrafts, setOverrideDrafts] = useState({});
  const [savingAccountIds, setSavingAccountIds] = useState([]);
  const [bulkAmount, setBulkAmount] = useState('');
  // A type="number" input reports "" for text it cannot parse (e.g. "5,000" or "1e"), which
  // is indistinguishable from blank. Track validity.badInput per field: 'bulk' and
  // `row:<account_id>`.
  const [badInputs, setBadInputs] = useState({});
  const skipCommitRef = useRef(false);

  useEffect(() => {
    fetchOperatingAccounts();
  }, []);

  const setBadInput = (key, bad) => {
    setBadInputs(prev => {
      if (!!prev[key] === bad) return prev;
      const next = { ...prev };
      if (bad) next[key] = true; else delete next[key];
      return next;
    });
  };

  const fetchOperatingAccounts = async () => {
    try {
      setOperatingLoading(true);
      setOperatingError(null);
      const response = await api.get('/api/accounts/operating-thresholds');
      setOperatingAccounts(response.data?.accounts || []);
      setOverrideDrafts({});
      setBadInputs({});
      setSelectedAccountIds([]);
    } catch (err) {
      setOperatingError(formatApiError(err, 'Failed to load operating accounts'));
    } finally {
      setOperatingLoading(false);
    }
  };

  const dropOverrideDrafts = (ids) => {
    setOverrideDrafts(prev => {
      const next = { ...prev };
      ids.forEach(id => { delete next[id]; });
      return next;
    });
    setBadInputs(prev => {
      const next = { ...prev };
      ids.forEach(id => { delete next[`row:${id}`]; });
      return next;
    });
  };

  // Resolves true when the server accepted the change. All-or-nothing server-side.
  const saveOverrides = async (accountIds, threshold) => {
    setSavingAccountIds(prev => [...prev, ...accountIds]);
    setOperatingError(null);
    try {
      const response = await api.patch('/api/accounts/low-balance-threshold', {
        account_ids: accountIds,
        threshold
      });
      const updatedById = {};
      (response.data?.updated || []).forEach(row => { updatedById[row.account_id] = row; });
      setOperatingAccounts(prev =>
        prev.map(a => (updatedById[a.account_id] ? { ...a, ...updatedById[a.account_id] } : a))
      );
      dropOverrideDrafts(accountIds);
      return true;
    } catch (err) {
      setOperatingError(formatApiError(err, 'Failed to save low-balance override'));
      return false;
    } finally {
      setSavingAccountIds(prev => prev.filter(id => !accountIds.includes(id)));
    }
  };

  const commitOverride = async (account) => {
    if (skipCommitRef.current) {
      skipCommitRef.current = false;
      return;
    }
    const draft = overrideDrafts[account.account_id];
    if (draft === undefined) return;
    const parsed = parseAmount(draft);
    if (badInputs[`row:${account.account_id}`] || (parsed !== null && !isValidAmount(parsed))) {
      setOperatingError('An override must be a number of 0 or more. Leave it blank to use the default.');
      return;
    }
    if (parsed === (account.low_balance_threshold ?? null)) {
      dropOverrideDrafts([account.account_id]);
      return;
    }
    await saveOverrides([account.account_id], parsed);
  };

  const handleBulkSet = async (ids) => {
    const amount = parseAmount(bulkAmount);
    if (badInputs.bulk || amount === null || !isValidAmount(amount)) {
      setOperatingError('Enter an override amount of 0 or more first.');
      return;
    }
    if (ids.length > MAX_BULK_ACCOUNTS) {
      setOperatingError(`Select at most ${MAX_BULK_ACCOUNTS} accounts at a time.`);
      return;
    }
    if (await saveOverrides(ids, amount)) setSelectedAccountIds([]);
  };

  const handleBulkClear = async (ids) => {
    if (ids.length > MAX_BULK_ACCOUNTS) {
      setOperatingError(`Select at most ${MAX_BULK_ACCOUNTS} accounts at a time.`);
      return;
    }
    if (await saveOverrides(ids, null)) setSelectedAccountIds([]);
  };

  const scopedOperatingAccounts = operatingAccounts.filter(a => allFirmsSelected || selectedFirms.includes(a.law_firm));
  const operatingFirmOptions = [...new Set(scopedOperatingAccounts.map(a => a.law_firm))];
  const activeFirmFilter = operatingFirmOptions.includes(operatingFirmFilter) ? operatingFirmFilter : '';
  const searchNeedle = operatingSearch.trim().toLowerCase();
  const visibleOperatingAccounts = scopedOperatingAccounts.filter(a => {
    if (activeFirmFilter && a.law_firm !== activeFirmFilter) return false;
    if (!searchNeedle) return true;
    return [a.law_firm, a.name, a.nickname, a.mask].some(v => v && String(v).toLowerCase().includes(searchNeedle));
  });
  const selectedIdSet = new Set(selectedAccountIds);
  // Bulk actions only touch rows that are currently visible and checked.
  const selectedVisibleIds = visibleOperatingAccounts.filter(a => selectedIdSet.has(a.account_id)).map(a => a.account_id);
  const allVisibleSelected = visibleOperatingAccounts.length > 0 && selectedVisibleIds.length === visibleOperatingAccounts.length;
  const bulkAmountValue = parseAmount(bulkAmount);
  const bulkAmountValid = !badInputs.bulk && bulkAmountValue !== null && isValidAmount(bulkAmountValue);
  const canBulkSet = selectedVisibleIds.length > 0 && bulkAmountValid && savingAccountIds.length === 0;

  return (
    <div>
      <h4 className="text-sm font-medium text-gray-700">Operating accounts for low-balance alerts</h4>
      <p className="mt-1 mb-3 text-xs text-gray-500">
        Overrides apply to every subscriber and save as soon as you change them, without waiting for this form's Save. A blank override uses the default above.
      </p>

      {operatingError && (
        <div role="alert" className="bg-red-50 border border-red-200 text-red-700 px-3 py-2 rounded-md mb-3 text-sm flex items-start">
          <AlertTriangle className="w-4 h-4 mr-2 mt-0.5 flex-shrink-0" />
          <span>{operatingError}</span>
        </div>
      )}

      {operatingLoading ? (
        <div className="text-center py-6 text-sm text-gray-600">Loading operating accounts...</div>
      ) : !allFirmsSelected && selectedFirms.length === 0 ? (
        <p className="text-sm text-gray-500 py-4">Select at least one law firm above to see its operating accounts.</p>
      ) : scopedOperatingAccounts.length === 0 ? (
        <p className="text-sm text-gray-500 py-4">No operating accounts for the selected law firms. Flag accounts as Operating in Account Summary to track them here. Accounts must also be linked and not hidden or removed.</p>
      ) : (
        <>
          <div className="flex flex-wrap items-center gap-2 mb-2">
            <div className="relative flex-1 min-w-[12rem]">
              <Search className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 transform -translate-y-1/2" />
              <input
                type="text"
                value={operatingSearch}
                onChange={(e) => setOperatingSearch(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') e.preventDefault(); }}
                placeholder="Search firm, account or last digits"
                form={STANDALONE_CONTROL_FORM}
                aria-label="Search operating accounts"
                className="w-full pl-9 pr-3 py-2 text-sm border border-gray-300 rounded-xl bg-gray-50 focus:outline-none focus:ring-1 focus:ring-brand-600 focus:border-brand-600"
              />
            </div>
            <select
              form={STANDALONE_CONTROL_FORM}
              value={activeFirmFilter}
              onChange={(e) => setOperatingFirmFilter(e.target.value)}
              aria-label="Filter by law firm"
              className="px-3 py-2 border border-gray-300 rounded-xl bg-gray-50 text-sm focus:outline-none focus:ring-1 focus:ring-brand-600 focus:border-brand-600"
            >
              <option value="">All firms in this subscription</option>
              {operatingFirmOptions.map(firm => (
                <option key={firm} value={firm}>{firm}</option>
              ))}
            </select>
          </div>

          <div className="flex flex-wrap items-center gap-2 mb-2 text-sm">
            <span className="text-gray-600" aria-live="polite">{selectedVisibleIds.length} selected</span>
            <div className="relative w-32">
              <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500">$</span>
              <input
                type="number"
                min="0"
                step="0.01"
                value={bulkAmount}
                onChange={(e) => {
                  setBulkAmount(e.target.value);
                  setBadInput('bulk', e.target.validity.badInput);
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    if (canBulkSet) handleBulkSet(selectedVisibleIds);
                  }
                }}
                placeholder="Amount"
                form={STANDALONE_CONTROL_FORM}
                aria-label="Override amount for selected accounts"
                className="w-full pl-7 pr-2 py-1.5 text-sm border border-gray-300 rounded-xl bg-gray-50 focus:outline-none focus:ring-1 focus:ring-brand-600 focus:border-brand-600"
              />
            </div>
            <button
              form={STANDALONE_CONTROL_FORM}
              type="button"
              onClick={() => handleBulkSet(selectedVisibleIds)}
              disabled={!canBulkSet}
              className="px-3 py-1.5 text-white rounded-full bg-brand-600 hover:bg-brand-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200"
            >
              {bulkAmountValid ? `Set override to ${formatCurrency(bulkAmountValue)}` : 'Set override'}
            </button>
            <button
              form={STANDALONE_CONTROL_FORM}
              type="button"
              onClick={() => handleBulkClear(selectedVisibleIds)}
              disabled={selectedVisibleIds.length === 0 || savingAccountIds.length > 0}
              className="px-3 py-1.5 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-full disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200"
            >
              Clear override
            </button>
          </div>

          <div className="border border-gray-200 rounded-xl overflow-auto max-h-96">
            <table className="min-w-full text-sm">
              <thead className="bg-gray-50 sticky top-0 z-10">
                <tr className="text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
                  <th scope="col" className="px-3 py-2 w-8">
                    <input
                      form={STANDALONE_CONTROL_FORM}
                      type="checkbox"
                      checked={allVisibleSelected}
                      ref={(el) => { if (el) el.indeterminate = selectedVisibleIds.length > 0 && !allVisibleSelected; }}
                      onChange={(e) => {
                        const visibleIds = visibleOperatingAccounts.map(a => a.account_id);
                        setSelectedAccountIds(prev => e.target.checked
                          ? [...new Set([...prev, ...visibleIds])]
                          : prev.filter(id => !visibleIds.includes(id)));
                      }}
                      aria-label="Select all visible accounts"
                      className="h-4 w-4 border-gray-300 rounded accent-brand-600"
                    />
                  </th>
                  <th scope="col" className="px-3 py-2">Law firm</th>
                  <th scope="col" className="px-3 py-2">Account</th>
                  <th scope="col" className="px-3 py-2 text-right">Balance</th>
                  <th scope="col" className="px-3 py-2">Override (applies to all subscribers)</th>
                  <th scope="col" className="px-3 py-2">Effective threshold</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 bg-white">
                {visibleOperatingAccounts.length === 0 ? (
                  <tr>
                    <td colSpan={6} className="px-3 py-6 text-center text-gray-500">No accounts match your search.</td>
                  </tr>
                ) : visibleOperatingAccounts.map((account) => {
                  const hasOverride = account.low_balance_threshold != null;
                  const effective = hasOverride ? account.low_balance_threshold : defaultThreshold;
                  const isBelow = effective !== null && account.current_balance < effective;
                  const saving = savingAccountIds.includes(account.account_id);
                  const changedAt = formatChangedAt(account.low_balance_threshold_updated_at);
                  const changedBy = account.low_balance_threshold_updated_by;
                  const draft = overrideDrafts[account.account_id];
                  const accountLabel = account.nickname || account.name;
                  return (
                    <tr key={account.account_id} className={isBelow ? 'bg-red-50' : ''}>
                      <td className="px-3 py-2">
                        <input
                          form={STANDALONE_CONTROL_FORM}
                          type="checkbox"
                          checked={selectedIdSet.has(account.account_id)}
                          onChange={(e) => setSelectedAccountIds(prev => e.target.checked
                            ? [...prev, account.account_id]
                            : prev.filter(id => id !== account.account_id))}
                          aria-label={`Select ${accountLabel}`}
                          className="h-4 w-4 border-gray-300 rounded accent-brand-600"
                        />
                      </td>
                      <td className="px-3 py-2 text-gray-900">{account.law_firm}</td>
                      <td className="px-3 py-2 whitespace-nowrap">
                        <div className="text-gray-900" title={account.name}>{accountLabel}</div>
                        {account.mask && <div className="text-xs text-gray-500">{`••${account.mask}`}</div>}
                      </td>
                      <td className="px-3 py-2 text-right whitespace-nowrap font-mono tabular-nums">
                        <span className={isBelow ? 'text-red-700 font-semibold' : 'text-gray-900'}>
                          {formatBalance(account.current_balance)}
                        </span>
                        {isBelow && (
                          <div className="text-xs font-sans font-medium text-red-700">Below threshold</div>
                        )}
                      </td>
                      <td className="px-3 py-2">
                        <div className="relative w-32">
                          <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500">$</span>
                          <input
                            type="number"
                            min="0"
                            step="0.01"
                            value={draft !== undefined ? draft : (account.low_balance_threshold ?? '')}
                            onChange={(e) => {
                              setOverrideDrafts(prev => ({ ...prev, [account.account_id]: e.target.value }));
                              setBadInput(`row:${account.account_id}`, e.target.validity.badInput);
                            }}
                            onBlur={() => commitOverride(account)}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter') {
                                e.preventDefault();
                                e.currentTarget.blur();
                              } else if (e.key === 'Escape') {
                                e.preventDefault();
                                skipCommitRef.current = true;
                                dropOverrideDrafts([account.account_id]);
                                e.currentTarget.blur();
                              }
                            }}
                            disabled={saving}
                            placeholder="Default"
                            form={STANDALONE_CONTROL_FORM}
                            aria-label={`Low-balance override for ${accountLabel}`}
                            className="w-full pl-7 pr-2 py-1.5 text-sm border border-gray-300 rounded-xl bg-gray-50 focus:outline-none focus:ring-1 focus:ring-brand-600 focus:border-brand-600 disabled:opacity-50"
                          />
                        </div>
                        {hasOverride && (changedBy || changedAt) && (
                          <div className="mt-1 text-xs text-gray-500" title={[changedBy && `Changed by ${changedBy}`, changedAt].filter(Boolean).join(' on ')}>
                            {[changedBy && `by ${changedBy}`, changedAt].filter(Boolean).join(', ')}
                          </div>
                        )}
                      </td>
                      <td className="px-3 py-2 whitespace-nowrap">
                        {effective === null ? (
                          <span className="text-gray-500" title="No default set and no override, so this account is not checked">Not checked</span>
                        ) : (
                          <>
                            <span className="font-mono tabular-nums text-gray-900">{formatCurrency(effective)}</span>
                            <span className={`ml-2 px-2 py-0.5 text-xs font-medium rounded-full ${
                              hasOverride ? 'bg-brand-50 text-brand-700' : 'bg-gray-100 text-gray-700'
                            }`}>
                              {hasOverride ? 'override' : 'default'}
                            </span>
                          </>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <p className="mt-2 text-xs text-gray-500">
            Showing {visibleOperatingAccounts.length} of {scopedOperatingAccounts.length} accounts. An account is flagged when its balance is below its effective threshold.
          </p>
        </>
      )}
    </div>
  );
};

export default OperatingThresholdsTable;
